import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ProfileContext, PhotoAnalysisResult } from '@/hooks/useAIChat';

interface AIChatHeaderProps {
  profile?: ProfileContext;
  photoAnalysis?: PhotoAnalysisResult;
  compact?: boolean;
  onClose?: () => void;
}

export function AIChatHeader({ profile, photoAnalysis, compact, onClose }: AIChatHeaderProps) {
  return (
    <div className={`flex-shrink-0 ${compact ? 'px-4 py-3 bg-transparent' : 'px-4 py-3 border-b border-brand-border bg-brand-surface'}`}>
      {/* Title Row */}
      <div className={`flex items-center gap-2 ${compact ? '' : 'mb-3'}`}>
        <div className="w-1.5 h-1.5 bg-brand-primary rounded-full shadow-[0_0_6px_1px] shadow-brand-primary/60" />
        <Sparkles className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-brand-primary`} />
        <h2 className={`${compact ? 'text-sm' : 'text-base'} font-medium text-white/80 flex-1`}>AI Dating Coach</h2>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 hover:bg-white/10">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Profile Context Badge - hidden in compact mode */}
      {profile && !compact && (
        <div className="flex items-center gap-3 px-4 py-2 bg-brand-background rounded-full w-fit mt-3">
          {profile.photos && profile.photos[0] && (
            <img
              src={profile.photos[0].url}
              alt={profile.displayName}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <span className="text-base text-brand-muted">
            Discussing <span className="text-white font-medium">{profile.displayName}</span>
            {profile.age && <span>, {profile.age}</span>}
          </span>
        </div>
      )}

      {/* Photo Analysis Summary - hidden in compact mode */}
      {photoAnalysis && !compact && (
        <div className="mt-2 p-2 bg-brand-primary/10 rounded-lg border border-brand-primary/20">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="w-3 h-3 text-brand-primary" />
            <span className="text-xs font-medium text-brand-primary">Photo Insights</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {photoAnalysis.conversationTopics?.slice(0, 3).map((topic, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-brand-surface text-xs text-brand-muted rounded-full"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
